import React, { useState, useEffect, useRef } from "react";
import {
  SupportRequestPageContainer,
  ContentWrapper,
  HeaderContainer,
  Title,
  ButtonWrapper,
} from "./SupportRequestPage.styles";
import Sidebar from "../../components/Sidebar";
import Table from "../../components/Table";
import {
  createSupportRequest,
  fetchSupportRequests,
  deleteSupportRequest,
} from "../../services/supportRequestService/supportRequestService";
import Button from "../../components/Buttons/Button";
import { CreateModal } from "../../components/Modals";
import { BaseLinkItem, FileLinkContainer } from "../LessonsPage/LessonsPage.styles";
import StatusBadge from "../../components/StatusBadge";
import LessonsButton from "../../components/Buttons/LessonsButton";
import { fileIcon } from "assets/icons";
import { FileIcon } from "../../components/DragAndDropField/DragAndDropField.styles";
import { SupportRequest } from "./supportRequestService.types";

import DeletedRow from "../../components/DeletedRow";
import RestoredRow from "../../components/RestoredRow";
import IconButton from "../../components/Buttons/IconButton";
import SupportResponsePage from "../SupportResponsePage/SupportResponsePage";
import { formatServerErrors } from "../../utils/errorHandler";

import { useNotify } from "context/NotificationContext";
import { logger } from "utils/logger";
import { useListPage } from "hooks/useListPage";
import { delIcon } from "assets/icons";
const SupportRequestPage: React.FC = () => {

  const {
    items: requests,
    setItems: setRequests,
    loading,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    totalPages,
    handleSort,
    reload: loadSupportRequests,
  } = useListPage<SupportRequest, Awaited<ReturnType<typeof fetchSupportRequests>>>({
    fetcher: ({ page, pageSize: size, orderBy }) => fetchSupportRequests(page, size, orderBy),
    select: (response) => ({ items: response.results, total: response.count }),
    errorMessage: "Ошибка загрузки запросов поддержки",
  });
  const [error, setError] = useState<string | null>(null);

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [externalErrors, setExternalErrors] = useState<Record<string, string>>({});
  const [pendingDelete, setPendingDelete] = useState<number | null>(null);
  const [recentlyRestored, setRecentlyRestored] = useState<number | null>(null);
  const [recentlyCreated, setRecentlyCreated] = useState<number | null>(null);
  const deleteTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const toast = useNotify();






  const handleDeleteRequest = (requestId: number) => {
    setPendingDelete(requestId);

    deleteTimeoutRef.current = setTimeout(async () => {
      try {
        await deleteSupportRequest(requestId);
        setPendingDelete(null);
        loadSupportRequests();
      } catch (error) {
        logger.error("Ошибка при удалении обращения:", error);
        toast.show({ severity: "error", summary: "Ошибка", detail: "Ошибка удаления обращения", life: 3000 });
      }
    }, 5000);
  };

  const restoreRequestHandler = (requestId: number) => {
    if (deleteTimeoutRef.current) {
      clearTimeout(deleteTimeoutRef.current);
      deleteTimeoutRef.current = null;
    }
    setPendingDelete(null);
    setRecentlyRestored(requestId);

    setTimeout(() => setRecentlyRestored(null), 3000);
  };

  const handleModalSubmit = async (data: Record<string, string | File[] | null>) => {
    setExternalErrors({});
    try {
      const payload = {
        question: data.question as string,
        topic: data.topic as string,
        material:
          Array.isArray(data.files) && data.files.length > 0 ? data.files[0] : null,
      };

      const response = await createSupportRequest(payload);
      setIsModalOpen(false);
      loadSupportRequests();
      setRecentlyCreated(response.id);

      setTimeout(() => setRecentlyCreated(null), 3000);
    } catch (err: any) {
      if (err.response && err.response.data) {
        const serverErrors = err.response.data;
        const formattedErrors = formatServerErrors(serverErrors);
        setExternalErrors(formattedErrors);
        
        // Показываем toast только для общих ошибок
        if (formattedErrors.error || formattedErrors.non_field_errors) {
          toast.show({
            severity: "error",
            summary: "Ошибка",
            detail: formattedErrors.error || formattedErrors.non_field_errors || "Ошибка отправки обращения",
            life: 3000,
          });
        }
      } else {
        logger.error("Ошибка при отправке обращения:", err);
        toast.show({ severity: "error", summary: "Ошибка", detail: "Ошибка отправки обращения", life: 3000 });
      }
    }
  };

  const getFileNameFromUrl = (url: string | null | undefined): string => {
    if (!url) return "";
    try {
      const urlPath = new URL(url).pathname;
      const fileName = urlPath.split("/").pop() || "";
      return decodeURIComponent(fileName);
    } catch {
      // Если URL не валидный, пытаемся извлечь имя файла другим способом
      const parts = url.split("/");
      return parts[parts.length - 1] || "";
    }
  };

  /*
    Ширины заданы явно: без них «Вопрос» получал 117 пикселей и уезжал
    на пять строк, а безымянная колонка с кнопкой — 184 впустую.
    Место распределялось по длине заголовка, а не по содержимому.
  */
  const headers = [
    { label: "Обновлено", key: "updated_at", fieldName: "updated_at", width: "12%" },
    { label: "Вопрос", key: "question", fieldName: "question", width: "26%" },
    { label: "Тема обращения", key: "topic", fieldName: "topic", width: "13%" },
    { label: "Материал к вопросу", key: "material", fieldName: "material", width: "15%" },
    { label: "Ответ", key: "response_status", fieldName: "response_status", width: "10%" },
    { label: "Содержание ответа", key: "response_content", fieldName: "response_content", width: "18%" },
    { label: "", key: "delete", sortable: false, fieldName: "", width: "6%" },
  ];

  const rows = requests.map((request) => {
    if (request.id === pendingDelete) {
      return {
        question: (
          <DeletedRow
            id={request.id}
            onRestore={restoreRequestHandler}
            colSpan={7}
            message={`Обращение "${request.question}" будет удалено через 5 секунд.`}
            targetText="Восстановить обращение"
          />
        ),
      };
    }

    return {
      updated_at: new Date(request.updated_at).toLocaleString("ru", {
        year: "2-digit",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      }),
      question: request.question,
      topic: request.topic,
      material: (
        <FileLinkContainer
          onClick={() => {
            if (request.material) {
              window.open(request.material, "_blank");
            } else {
              logger.error("URL для файла отсутствует");
              toast.show({ severity: "error", summary: "Ошибка", detail: "URL для файла отсутствует", life: 3000 });
            }
          }}
          style={{ cursor: request.material ? "pointer" : "default" }}
          title={request.material ? getFileNameFromUrl(request.material) : ""}
        >
          {request.material && (
            <BaseLinkItem>
              <FileIcon as={fileIcon} aria-hidden="true" />
            </BaseLinkItem>
          )}
        </FileLinkContainer>
      ),
      response_status: (
        <StatusBadge
          text={request.response_status ? "Получен" : "Ожидается"}
          variant={request.response_status ? "green" : "red"}
        />
      ),
      response_content: request.response_status ? (
        <LessonsButton
          href={`/support-response/${request.id}`}
          text="Смотреть"
        />
      ) : (
        <LessonsButton href="#" text="Нет ответа" noLessons={true} />
      ),
      delete: (
        <IconButton
          icon={delIcon}
          tone="danger"
          altText="Удалить"
          onClick={() => handleDeleteRequest(request.id)}
        />
      ),
    };
  });

  return (
    <SupportRequestPageContainer>
      <Sidebar />
      <ContentWrapper>
        <HeaderContainer>
          <Title>Поддержка</Title>
          <ButtonWrapper>
            <Button
              variant="primary"
              onClick={() => setIsModalOpen(true)}
              size="lg"
            >
              Задать вопрос
            </Button>
          </ButtonWrapper>
        </HeaderContainer>
          <Table
            emptyState={{
              title: "Обращений пока нет",
              description:
                "Сюда попадают вопросы от ваших учеников.",
            }}
            headers={headers}
            rows={rows}
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            pageSize={pageSize}
            onPageSizeChange={setPageSize}
            onSort={handleSort}
            rowAmountOptions={[
              { label: "7 запросов", value: 7 },
              { label: "15 запросов", value: 15 },
              { label: "20 запросов", value: 20 },
            ]}
            specialRowKey="question"
            loading={loading}
          />
        {isModalOpen && (
          <CreateModal
            title="Поддержка"
            fields={[
              {
                name: "topic",
                type: "text",
                placeholder: "Тема обращения",
                required: true,
              },
              {
                name: "question",
                type: "textarea",
                placeholder: "Вопрос",
                required: true,
              },
            ]}
            hasDragAndDrop={true}
            onSubmit={handleModalSubmit}
            onCancel={() => setIsModalOpen(false)}
            disableToggleSwitch={true}
            submitButtonText={"Задать вопрос"}
            maxFiles={1}
            externalErrors={externalErrors}
          />
        )}
      </ContentWrapper>
    </SupportRequestPageContainer>
  );
};

export default SupportRequestPage;
